import { InputSelect } from '@farmshare/ui-components';
import { Box, Stack, Typography } from '@mui/material';
import { EventRepeat } from '@mui/icons-material';
import { useFormikContext } from 'formik';
import { get } from 'lodash';
import moment from 'moment';

import { RecurrenceRuleInput } from 'lib/graphql';

// dropoffDate is stored as midnight UTC (formatStringToMidnightUTC), so the
// recurrence is anchored in UTC to match. ProcessorScheduling is day-granular,
// and anchoring at UTC noon keeps each occurrence on its intended calendar day.
const DEFAULT_TZ = 'UTC';

// moment day(): 0=Sun..6=Sat → RFC 5545 weekday codes.
const WEEKDAY_CODES = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'] as const;

export interface RecurrenceForm {
  frequency: 'WEEKLY' | 'MONTHLY';
  interval: string; // 1..4, parsed on submit
  monthlyMode: 'dayOfMonth' | 'weekday';
  endMode: 'never' | 'count';
  count: string; // 3 | 6 | 12
}

export const recurrenceInitialValues: RecurrenceForm = {
  frequency: 'WEEKLY',
  interval: '1',
  monthlyMode: 'dayOfMonth',
  endMode: 'never',
  count: '12',
};

// A stored/queried recurrence rule (subset of fields the UI reads).
export interface RuleLike {
  freq: string;
  interval?: number | null;
  anchor?: string | null;
  byDay?: ({ ordinal?: number | null; day: string } | null)[] | null;
  byMonthDay?: (number | null)[] | null;
  until?: string | null;
  count?: number | null;
}

const DAY_NAMES: Record<string, string> = {
  SU: 'Sunday',
  MO: 'Monday',
  TU: 'Tuesday',
  WE: 'Wednesday',
  TH: 'Thursday',
  FR: 'Friday',
  SA: 'Saturday',
};
const ORDINALS = ['1st', '2nd', '3rd', '4th', '5th'];

/** Human summary of a stored rule, e.g. "Repeats every 2 weeks on Monday". */
export function describeRule(rule: RuleLike): string {
  const interval = Math.max(1, rule.interval ?? 1);
  const every = interval === 1 ? '' : ` ${interval}`;
  const plural = interval === 1 ? '' : 's';

  let cadence: string;
  if (rule.freq === 'WEEKLY') {
    const day = rule.byDay?.[0]?.day;
    cadence = `every${every} week${plural}${
      day ? ` on ${DAY_NAMES[day] ?? day}` : ''
    }`;
  } else if (rule.freq === 'MONTHLY') {
    const monthDay = rule.byMonthDay?.[0];
    const ordinalEntry = rule.byDay?.[0];
    if (monthDay != null) {
      cadence = `every${every} month${plural} on the ${moment
        .utc('2020-01-01')
        .date(monthDay)
        .format('Do')}`;
    } else if (ordinalEntry?.ordinal) {
      cadence = `every${every} month${plural} on the ${
        ORDINALS[ordinalEntry.ordinal - 1] ?? `${ordinalEntry.ordinal}th`
      } ${DAY_NAMES[ordinalEntry.day] ?? ordinalEntry.day}`;
    } else {
      cadence = `every${every} month${plural}`;
    }
  } else {
    cadence = `every${every} ${rule.freq.toLowerCase()}${plural}`;
  }

  let ending = '';
  if (rule.count) {
    ending = `, ${rule.count} times`;
  } else if (rule.until) {
    ending = `, until ${moment.utc(rule.until).format('LL')}`;
  }
  return `Repeats ${cadence}${ending}`;
}

/** Convert a stored rule back into editable form values. */
export function ruleToForm(rule: RuleLike): RecurrenceForm {
  return {
    frequency: rule.freq === 'MONTHLY' ? 'MONTHLY' : 'WEEKLY',
    interval: String(Math.max(1, rule.interval ?? 1)),
    monthlyMode: rule.byMonthDay?.length ? 'dayOfMonth' : 'weekday',
    endMode: rule.count ? 'count' : 'never',
    count: rule.count ? String(rule.count) : '12',
  };
}

/** The anchor day (YYYY-MM-DD) a rule is built around. */
export function anchorDateFromRule(rule: RuleLike): string {
  return rule.anchor
    ? rule.anchor.slice(0, 10)
    : moment.utc().format('YYYY-MM-DD');
}

/** Build the RecurrenceRuleInput from the form + the seed job's dropoff date. */
export function buildRecurrenceRule(
  values: RecurrenceForm,
  dropoffDate: string,
): RecurrenceRuleInput {
  const d = moment.utc(dropoffDate);
  const dayCode = WEEKDAY_CODES[d.day()];

  const rule: RecurrenceRuleInput = {
    freq: values.frequency,
    interval: Math.max(1, parseInt(values.interval, 10) || 1),
    anchor: `${d.format('YYYY-MM-DD')}T12:00:00`,
    timezone: DEFAULT_TZ,
  };

  if (values.frequency === 'WEEKLY') {
    rule.byDay = [{ day: dayCode }];
  } else if (values.monthlyMode === 'dayOfMonth') {
    rule.byMonthDay = [d.date()];
  } else {
    // Nth weekday of the month, derived from the job's date.
    const ordinal = Math.ceil(d.date() / 7);
    rule.byDay = [{ ordinal, day: dayCode }];
  }

  if (values.endMode === 'count') {
    rule.count = Math.max(1, parseInt(values.count, 10) || 12);
  }

  return rule;
}

/** Prefix a field name so the same controls work flat or nested under a key. */
function fieldName(prefix: string | undefined, field: keyof RecurrenceForm) {
  return prefix ? `${prefix}.${field}` : field;
}

/**
 * Options for "how many future jobs to keep scheduled ahead" (the series'
 * targetUpcomingCount). 'Automatic' clears it → time-based horizon. Stored as a
 * string in Formik; convert with `parseKeepScheduled` on submit.
 */
export const KEEP_SCHEDULED_OPTIONS = [
  { label: 'Automatic', value: '' },
  ...[2, 3, 4, 5, 6, 8, 10, 12].map((n) => ({
    label: String(n),
    value: String(n),
  })),
];

/** Convert the keep-scheduled select value to the API's number | null. */
export function parseKeepScheduled(value?: string | null): number | null {
  if (!value) return null;
  const n = parseInt(value, 10);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** Human summary of the chosen cadence, shown live in the modal. */
function CadenceSummary({
  dropoffDate,
  prefix,
}: {
  dropoffDate: string;
  prefix?: string;
}) {
  const { values } = useFormikContext<unknown>();
  const form = (prefix ? get(values, prefix) : values) as RecurrenceForm;
  const d = moment.utc(dropoffDate);
  const interval = Math.max(1, parseInt(form?.interval, 10) || 1);
  const every = interval === 1 ? '' : ` ${interval}`;

  let cadence: string;
  if (form?.frequency === 'WEEKLY') {
    cadence = `every${every} week${interval === 1 ? '' : 's'} on ${d.format(
      'dddd',
    )}`;
  } else if (form?.monthlyMode === 'dayOfMonth') {
    cadence = `every${every} month${interval === 1 ? '' : 's'} on the ${d.format(
      'Do',
    )}`;
  } else {
    const ordinal = ['1st', '2nd', '3rd', '4th', '5th'][
      Math.ceil(d.date() / 7) - 1
    ];
    cadence = `every${every} month${
      interval === 1 ? '' : 's'
    } on the ${ordinal} ${d.format('dddd')}`;
  }

  const ending =
    form?.endMode === 'count' ? `, for ${form.count} occurrences` : '';

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 1.5,
        py: 1,
        borderRadius: 1,
        bgcolor: 'action.hover',
        color: 'text.secondary',
      }}
    >
      <EventRepeat fontSize="small" />
      <Typography variant="body2">
        Repeats {cadence}
        {ending}.
      </Typography>
    </Box>
  );
}

/**
 * The cadence controls (frequency / interval / monthly mode / ends) plus a live
 * summary. Reads its values either from the top-level Formik form (`prefix`
 * omitted) or from a nested object at `prefix` (e.g. `recurrence`).
 */
export function RecurrenceFields({
  dropoffDate,
  prefix,
  showKeepScheduled = false,
}: {
  dropoffDate: string;
  prefix?: string;
  /** Render the "jobs to keep scheduled ahead" (targetUpcomingCount) control. */
  showKeepScheduled?: boolean;
}) {
  const { values } = useFormikContext<unknown>();
  const form = (prefix ? get(values, prefix) : values) as RecurrenceForm;
  // Related controls are paired on a row (they collapse to a single column on
  // narrow surfaces, e.g. the nested Review step in the quick-schedule modal).
  const row = { xs: 'column', sm: 'row' } as const;
  return (
    <Stack spacing={2}>
      <Stack spacing={2} direction={row}>
        <Box sx={{ flex: 1 }}>
          <InputSelect
            label="Frequency"
            nameOverride={fieldName(prefix, 'frequency')}
            options={[
              { label: 'Weekly', value: 'WEEKLY' },
              { label: 'Monthly', value: 'MONTHLY' },
            ]}
            floatingLabel
          />
        </Box>
        <Box sx={{ flex: 1 }}>
          <InputSelect
            label="Interval"
            nameOverride={fieldName(prefix, 'interval')}
            options={[
              { label: 'Every', value: '1' },
              { label: 'Every other', value: '2' },
              { label: 'Every 3rd', value: '3' },
              { label: 'Every 4th', value: '4' },
            ]}
            floatingLabel
          />
        </Box>
      </Stack>
      {form?.frequency === 'MONTHLY' && (
        <InputSelect
          label="On"
          nameOverride={fieldName(prefix, 'monthlyMode')}
          options={[
            { label: 'The same day of the month', value: 'dayOfMonth' },
            { label: 'The same weekday (e.g. 2nd Thursday)', value: 'weekday' },
          ]}
          floatingLabel
        />
      )}
      <Stack spacing={2} direction={row}>
        <Box sx={{ flex: 1 }}>
          <InputSelect
            label="Ends"
            nameOverride={fieldName(prefix, 'endMode')}
            options={[
              { label: 'Never', value: 'never' },
              { label: 'After a number of jobs', value: 'count' },
            ]}
            floatingLabel
          />
        </Box>
        {form?.endMode === 'count' && (
          <Box sx={{ flex: 1 }}>
            <InputSelect
              label="Number of jobs"
              nameOverride={fieldName(prefix, 'count')}
              options={[
                { label: '3', value: '3' },
                { label: '6', value: '6' },
                { label: '12', value: '12' },
              ]}
              floatingLabel
            />
          </Box>
        )}
        {showKeepScheduled && (
          <Box sx={{ flex: 1 }}>
            <InputSelect
              label="Jobs to keep scheduled ahead"
              nameOverride={
                prefix ? `${prefix}.targetUpcomingCount` : 'targetUpcomingCount'
              }
              options={KEEP_SCHEDULED_OPTIONS}
              floatingLabel
            />
          </Box>
        )}
      </Stack>
      <CadenceSummary dropoffDate={dropoffDate} prefix={prefix} />
    </Stack>
  );
}
